import React, { useEffect, useRef } from 'react';
import { SubscribeReturn, TimelineMarkersConsumer } from '../TimelineMarkersContext';
import { MarkerRendererType, MarkerType, TimelineMarkerType } from '../markerType';

export interface TodayMarkerProps {
  interval?: number;
  updateMarker: (marker: MarkerType) => void;
  subscribeMarker: (m: MarkerType) => SubscribeReturn;
  children?: MarkerRendererType;
}

const DEFAULT_INTERVAL = 1000 * 10; // 10 seconds

function TodayMarkerInner({
  interval = DEFAULT_INTERVAL,
  updateMarker,
  subscribeMarker,
  children,
}: TodayMarkerProps) {
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const getMarkerRef = useRef<(() => MarkerType) | null>(null);

  useEffect(() => {
    const { unsubscribe, getMarker } = subscribeMarker({
      type: TimelineMarkerType.Today,
      renderer: children,
      interval,
    });
    unsubscribeRef.current = unsubscribe;
    getMarkerRef.current = getMarker;
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [subscribeMarker, interval, children]);

  useEffect(() => {
    if (getMarkerRef.current) {
      const marker = getMarkerRef.current();
      updateMarker({ ...marker, interval });
    }
  }, [interval, updateMarker]);

  return null;
}

export type TodayMarkerWrapperProps = Omit<TodayMarkerProps, 'updateMarker' | 'subscribeMarker'>;

function TodayMarkerWrapper(props: TodayMarkerWrapperProps) {
  return (
    <TimelineMarkersConsumer>
      {({ subscribeMarker, updateMarker }) => (
        <TodayMarkerInner
          subscribeMarker={subscribeMarker}
          updateMarker={updateMarker}
          {...props}
        />
      )}
    </TimelineMarkersConsumer>
  );
}

TodayMarkerWrapper.displayName = 'TodayMarker';

export default TodayMarkerWrapper;
